'use client';

import ECharts from '#/components/ECharts';
import { useTranslation } from '#/contexts/useTranslation';
import useNetworks from '#/data/useNetworks';
import useWatcherNetworks from '#/data/useWatcherNetworks';
import useWatcherPriceList from '#/data/useWatcherPriceList';
import { EChartsOption } from 'echarts';
import { ethers } from 'ethers';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';

export function PortfolioDistributionChart() {
  const translation = useTranslation();
  const { data: networksData } = useNetworks();
  const { data: watcherNetworksData } = useWatcherNetworks();
  const { data: watcherPriceInfoData } = useWatcherPriceList();
  const seriesData = useMemo(
    () =>
      networksData?.data.data &&
      watcherPriceInfoData &&
      watcherNetworksData?.data.data
        ? watcherNetworksData.data.data?.map(current => {
            const { decimal, name } = networksData.data.data?.find(
              item => item.symbol === current.symbol
            )!;
            const { price } = watcherPriceInfoData.data.data?.find(
              item => item.symbol === current.symbol
            )!;
            const valueAsBigNumber = ethers.FixedNumber.from(
              current.totalDelegated
            )
              .addUnsafe(ethers.FixedNumber.from(current.totalReward))
              .mulUnsafe(ethers.FixedNumber.from(price))
              .divUnsafe(
                ethers.FixedNumber.from(
                  ethers.BigNumber.from('10').pow(decimal)
                )
              );

            return {
              name,
              value: valueAsBigNumber.toUnsafeFloat(),
            };
          })
        : [],
    [watcherNetworksData, networksData, watcherPriceInfoData]
  );
  const orderedSeriesData = useMemo(
    () => orderBy(seriesData, ['value'], ['desc']),
    [seriesData]
  );
  const total = useMemo(
    () =>
      seriesData.reduce((acc, current) => {
        return acc + current.value;
      }, 0),
    [seriesData]
  );
  const labels = useMemo(() => {
    return Object.fromEntries(
      seriesData.map(item => {
        const val = Math.round((item.value * 10000) / total) / 100;
        const textVal =
          val < 0.01
            ? '<0.01%'
            : val > 99.99 && val < 100
            ? '>99.99%'
            : `${val}%`;
        return [item.name, `${item.name} ${textVal}`];
      })
    );
  }, [seriesData, total]);

  const option = useMemo<EChartsOption>(
    () => ({
      color: [
        '#698FF3',
        '#B4C7F9',
        '#68E4BD',
        '#B3F1DE',
        '#2FD4D4',
        '#82E5E5',
        '#C1F2F2',
        '#42DDAC',
        '#8EEBCD',
        '#C6F5E6',
        '#7BE09A',
        '#AFEBC6',
        '#D7F5E3',
      ],
      grid: {
        top: 16,
        right: 200,
        bottom: 0,
        left: 0,
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        left: '50%',
        top: 'middle',
        bottom: 25,
        itemGap: 20,
        textStyle: {
          fontSize: '1rem',
          padding: 6,
        },
        itemWidth: 14,
        formatter(name) {
          return labels[name];
        },
      },
      tooltip: {
        trigger: 'item',
        // formatter: '{b}',
        formatter(params) {
          // @ts-ignore
          const { name } = params;
          return labels[name];
        },
      },
      series: [
        {
          // name: 'Total Delegated Assets',
          data: orderedSeriesData,
          type: 'pie',
          radius: ['60%', '90%'],
          center: ['25%', '50%'],
          label: {
            show: false,
          },
          itemStyle: {
            // borderRadius: 4,
          },
          emphasis: {
            label: {
              show: false,
            },
          },
        },
      ],
    }),
    [orderedSeriesData, labels]
  );
  return (
    <section className="bg-white dark:bg-gray-700 border border-slate-200 rounded-2xl p-4 @3xl/main:p-6 space-y-4 flex flex-col h-full">
      <header>
        <span className="text-sm @3xl/main:text-xl font-bold truncate">
          {translation.PortfolioDashboard.PortfolioDistribution.Heading}
        </span>
      </header>
      <article className="flex-auto overflow-auto">
        {seriesData.length === 0 ? (
          <SkeletonPieChart className="w-full h-full pointer-events-none min-w-[400px]" />
        ) : (
          <ECharts option={option} className="w-full h-full min-w-[400px]" />
        )}
      </article>
    </section>
  );
}

interface SkeletonPieChartProps {
  className?: string;
}
function SkeletonPieChart({ className }: SkeletonPieChartProps) {
  const option = useMemo<EChartsOption>(
    () => ({
      color: ['#A0AEC0', '#CBD5E0', '#E2E8F0', '#EDF2F7'],
      grid: {
        top: 16,
        right: 200,
        bottom: 0,
        left: 0,
      },
      legend: {
        orient: 'vertical',
        left: '50%',
        top: 'middle',
        bottom: 25,
        itemGap: 20,
        textStyle: {
          fontSize: '0.75rem',
          color: '#E2E8F0',
          padding: 6,
        },
        itemWidth: 14,
        formatter: () => '███ ██',
      },
      series: [
        {
          data: [
            { value: 1048, name: '1' },
            { value: 735, name: '2' },
            { value: 580, name: '3' },
            { value: 284, name: '4' },
          ],
          type: 'pie',
          radius: ['60%', '90%'],
          center: ['25%', '50%'],
          label: {
            show: false,
          },
          emphasis: {
            scale: false,
          },
        },
      ],
    }),
    []
  );
  return <ECharts option={option} className={className} />;
}
